'use client'

import type { ReactNode } from 'react'

import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { DashboardLayout } from '@/components/blocks/app-layout'
import { PageLayoutTemplate } from '@/components/templates/page-layouts'
import { DrawerTemplate } from '@/components/templates/drawers'
import { KanbanTemplate } from '@/components/templates/kanban'
import { SupporterResponseTemplate } from '@/components/templates/supporter-response'
import { SupporterResponseWorkspace } from '@/components/blocks/supporter-response'
import { WritingStyleSettings } from '@/components/blocks/writing-style-settings'
import { ChatContactListView, ChatConversationStatesView, ChatSupporterProfileView } from '@/components/docs/chat-views'

import {
    AppShell,
    AppSidebar,
    AppTopbar,
    BoardLayout,
    LayoutColumns,
    LayoutContainer,
    PageBody,
    PageHeader,
    WorkspaceLayout
} from '@/components/blocks/app-layout'

import {
    ChartAcquisitionChannels,
    ChartCompact,
    ChartRevenueOverview,
    ChartSubscriberTrends
} from '@/components/blocks/chart-blocks'

import {
    AcquisitionMonthContent,
    ActionsFooterSelect,
    ActivityComment,
    CommentComposer,
    DrawerFieldRow,
    DrawerMetric,
    EditorContent,
    ExportFooterAction,
    FacebookDirectContent,
    MessageRow,
    PreviewRegion,
    RevenuePeopleContent,
    SubscriberTag,
    SupporterRow
} from '@/components/blocks/drawer-blocks'

import {
    INBOX_ORGANIZATIONS,
    KanbanBoard,
    KanbanCard,
    KanbanColumn,
    KanbanEmptyBoard,
    KanbanOrganizationHeader,
    KanbanStages,
    KanbanSwimlane,
    KanbanToolbar
} from '@/components/blocks/kanban'

import {
    TableAiLinks,
    TableDefault,
    TableSupporters,
    TableToolPerformance,
    TableToolSubscribers
} from '@/components/blocks/tables'

/*
 * What each /view/<id> page renders. padded views sit on the page surface with 32px padding;
 * the rest fill the screen (shells and templates).
 */
interface ViewRender {
    padded: boolean
    render: () => ReactNode
}

function Caption({ children }: { children: ReactNode }) {
    return (
        <p className='text-sm font-medium text-text-secondary'>
            {children}
        </p>
    )
}

function Specimen({ label, children }: { label: string, children: ReactNode }) {
    return (
        <section className='flex flex-col gap-3'>
            <Caption>{label}</Caption>
            {children}
        </section>
    )
}

function RecipePanel({ title, description, children }: { title: string, description: string, children: ReactNode }) {
    return (
        <div className='mx-auto flex w-full max-w-140 flex-col rounded-xl border border-border-default bg-surface-raised shadow-overlay'>
            <div className='flex flex-col gap-1 border-b border-border-subtle p-6'>
                <h2 className='font-heading text-2xl font-semibold text-text-primary'>
                    {title}
                </h2>
                <p className='text-sm text-text-secondary'>
                    {description}
                </p>
            </div>
            <div className='flex flex-col gap-6 p-6'>
                {children}
            </div>
        </div>
    )
}

function DrawerPartsView() {
    const [status, setStatus] = useState('Awaiting review')
    const [comments, setComments] = useState([{ author: 'Adelina', time: '2 minutes ago', text: 'Just received my acceptance letter!' }])

    return (
        <div className='mx-auto flex w-full max-w-140 flex-col gap-8'>
            <Specimen label='MetricCard/Compact'>
                <div className='grid grid-cols-1 gap-3 sm:grid-cols-2'>
                    <DrawerMetric label='Revenue' value='$21,000' />
                    <DrawerMetric label='Revenue' value='$21,000' people={5} />
                </div>
            </Specimen>
            <Specimen label='Field/Row'>
                <DrawerFieldRow label='Field label' htmlFor='parts-field'>
                    <Input id='parts-field' defaultValue='Value' />
                </DrawerFieldRow>
            </Specimen>
            <Specimen label='SheetSection heading'>
                <h3 className='font-heading text-lg font-semibold text-text-primary'>
                    Section heading
                </h3>
            </Specimen>
            <Specimen label='SupporterRow'>
                <div className='flex flex-col'>
                    <SupporterRow name='Oliver Wilson' amount='$2,200' detail='Jan 2' />
                    <SupporterRow name='Oliver Wilson' amount='$2,200' detail='5 donors' rows={[{ name: 'Oliver Wilson', amount: '$2,200', share: '40%' }]} defaultOpen />
                </div>
            </Specimen>
            <Specimen label='Message row'>
                <MessageRow name='Oliver Wilson' date='Jan 2' message="Let's set up a meeting to discuss further." />
            </Specimen>
            <Specimen label='Subscriber tags'>
                <div className='flex gap-4'>
                    <SubscriberTag />
                    <SubscriberTag connected={false} />
                </div>
            </Specimen>
            <Specimen label='Activity/Comment and CommentComposer'>
                <div className='flex flex-col gap-4'>
                    {comments.map((comment, index) => <ActivityComment key={index} {...comment} />)}
                    <CommentComposer onSubmit={text => setComments(current => [...current, { author: 'You', time: 'just now', text }])} />
                </div>
            </Specimen>
            <Specimen label='PreviewRegion'>
                <PreviewRegion />
            </Specimen>
            <Specimen label='Footer actions'>
                <div className='flex flex-col gap-4 rounded-xl border border-border-subtle bg-surface-raised p-6'>
                    <ExportFooterAction />
                    <div className='flex justify-end gap-2'>
                        <Button variant='outline'>Cancel</Button>
                        <Button>Save changes</Button>
                    </div>
                    <ActionsFooterSelect value={status} onValueChange={setStatus} />
                </div>
            </Specimen>
        </div>
    )
}

function KanbanPartsView() {
    const [filters, setFilters] = useState(['Grow', 'Red Cross'])
    const [open, setOpen] = useState(false)
    const card = { title: 'Create social media content', category: 'content' as const, assignee: 'Adelina Hoxha' }

    return (
        <div className='flex flex-col gap-8'>
            <Specimen label='Kanban/Card: default, hover and selected'>
                <div className='grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-3'>
                    <KanbanCard task={card} />
                    <KanbanCard task={card} className='bg-surface-subtle' />
                    <KanbanCard task={card} selected />
                </div>
            </Specimen>
            <Specimen label='Kanban/Stages'>
                <KanbanStages className='max-w-3xl' />
            </Specimen>
            <Specimen label='Kanban/Column'>
                <div className='grid max-w-3xl grid-cols-1 gap-4 sm:grid-cols-2'>
                    <KanbanColumn status='awaiting-review' count={0} />
                    <KanbanColumn status='awaiting-review' count={1}>
                        <KanbanCard task={card} />
                    </KanbanColumn>
                </div>
            </Specimen>
            <Specimen label='Kanban/OrganizationHeader'>
                <div className='max-w-3xl'>
                    <KanbanOrganizationHeader name='Organization name' count={0} open={open} onToggle={() => setOpen(!open)} />
                </div>
            </Specimen>
            <Specimen label='Kanban/Toolbar'>
                <KanbanToolbar filters={filters} onFiltersChange={setFilters} className='max-w-3xl' />
            </Specimen>
        </div>
    )
}

const emptyOrganization = { id: 'organization', name: 'Organization name', logo: null, tasks: [] }

export const VIEW_RENDERS: Record<string, ViewRender> = {
    'dashboard-overview': { padded: false, render: () => <DrawerTemplate kind='revenue-people' defaultOpen={false} /> },
    'app-sidebar': {
        padded: false,
        render: () => (
            <div className='flex h-dvh bg-surface-page'>
                <AppSidebar />
            </div>
        )
    },
    'app-topbar': {
        padded: true,
        render: () => (
            <div className='flex flex-col gap-8'>
                <Specimen label='Default'>
                    <AppTopbar title='Page title' className='rounded-lg' />
                </Specimen>
                <Specimen label='With the date control'>
                    <AppTopbar title='Inbox' showDate className='rounded-lg' />
                </Specimen>
            </div>
        )
    },
    'page-header': {
        padded: true,
        render: () => (
            <div className='flex flex-col gap-10'>
                <PageHeader title='Page title' />
                <PageHeader
                    title='Inbox'
                    description='AI-recommended actions, reviewed and approved by your GoodUnited team'
                    actions={<Button>Add action</Button>}
                />
            </div>
        )
    },
    'layout-container': {
        padded: true,
        render: () => (
            <div className='grid grid-cols-1 gap-6 md:grid-cols-3'>
                <Specimen label='card'>
                    <LayoutContainer className='min-h-48' />
                </Specimen>
                <Specimen label='panel'>
                    <LayoutContainer variant='panel' className='min-h-48' />
                </Specimen>
                <Specimen label='lane'>
                    <LayoutContainer variant='lane' className='min-h-48' />
                </Specimen>
            </div>
        )
    },
    'layout-columns': {
        padded: true,
        render: () => (
            <div className='flex flex-col gap-8'>
                {([['1', 1], ['2', 2], ['3', 3], ['4', 4], ['2:1', 2], ['1:2', 2]] as const).map(([track, count]) => (
                    <Specimen key={track} label={`track ${track}`}>
                        <LayoutColumns track={track}>
                            {Array.from({ length: count }, (_, index) => <LayoutContainer key={index} className='min-h-28' />)}
                        </LayoutColumns>
                    </Specimen>
                ))}
            </div>
        )
    },
    'page-bodies': {
        padded: false,
        render: () => (
            <div className='flex flex-col gap-px bg-border-subtle'>
                <div className='min-h-105'>
                    <PageBody title='Page title'>
                        <LayoutContainer className='min-h-48' />
                    </PageBody>
                </div>
                <WorkspaceLayout className='min-h-105' />
            </div>
        )
    },
    'composed-layouts': {
        padded: true,
        render: () => (
            <div className='flex flex-col gap-10'>
                <Specimen label='Dashboard'>
                    <DashboardLayout />
                </Specimen>
                <Specimen label='Workspace'>
                    <WorkspaceLayout className='min-h-105 overflow-hidden rounded-xl' />
                </Specimen>
                <Specimen label='Board'>
                    <BoardLayout />
                </Specimen>
            </div>
        )
    },
    'app-shell': {
        padded: false,
        render: () => (
            <AppShell title='Page title'>
                <PageBody title='Page title' />
            </AppShell>
        )
    },
    'chart-area-compact': { padded: true, render: () => <ChartCompact variant='area' /> },
    'chart-line-compact': { padded: true, render: () => <ChartCompact variant='line' /> },
    'chart-subscriber-trends': { padded: true, render: () => <ChartSubscriberTrends /> },
    'chart-acquisition-channels': { padded: true, render: () => <ChartAcquisitionChannels /> },
    'chart-revenue-overview': { padded: true, render: () => <ChartRevenueOverview /> },
    'drawer-parts': { padded: true, render: () => <DrawerPartsView /> },
    'recipe-revenue-people': {
        padded: true,
        render: () => (
            <RecipePanel title='Revenue · People' description='Revenue by fundraiser'>
                <RevenuePeopleContent />
            </RecipePanel>
        )
    },
    'recipe-facebook-direct': {
        padded: true,
        render: () => (
            <RecipePanel title='Facebook Direct' description='People behind the selected source'>
                <FacebookDirectContent />
            </RecipePanel>
        )
    },
    'recipe-acquisition-month': {
        padded: true,
        render: () => (
            <RecipePanel title='January 2026' description='New supporters by acquisition channel'>
                <AcquisitionMonthContent />
            </RecipePanel>
        )
    },
    'recipe-editor': {
        padded: true,
        render: () => (
            <RecipePanel title='World Kidney Day' description='Fundraiser page · National Kidney Foundation'>
                <EditorContent />
            </RecipePanel>
        )
    },
    'kanban-parts': { padded: true, render: () => <KanbanPartsView /> },
    'kanban-swimlane': {
        padded: true,
        render: () => (
            <div className='no-scrollbar overflow-x-auto'>
                <div className='flex min-w-220 flex-col gap-4'>
                    <KanbanSwimlane organization={emptyOrganization} />
                    <KanbanSwimlane organization={{ ...emptyOrganization, id: 'organization-open' }} defaultOpen />
                </div>
            </div>
        )
    },
    'kanban-inbox': { padded: true, render: () => <KanbanBoard organizations={INBOX_ORGANIZATIONS} /> },
    'kanban-empty': { padded: true, render: () => <KanbanEmptyBoard /> },
    'table-default': { padded: true, render: () => <TableDefault /> },
    'table-supporters': { padded: true, render: () => <TableSupporters /> },
    'table-ai-links': { padded: true, render: () => <TableAiLinks /> },
    'table-tool-performance': { padded: true, render: () => <TableToolPerformance /> },
    'table-tool-subscribers': { padded: true, render: () => <TableToolSubscribers /> },
    'chat-contact-list': { padded: true, render: () => <ChatContactListView /> },
    'chat-conversation-states': { padded: true, render: () => <ChatConversationStatesView /> },
    'chat-supporter-profile': { padded: true, render: () => <ChatSupporterProfileView /> },
    'chat-workspace': {
        padded: false,
        render: () => (
            <div className='h-dvh'>
                <SupporterResponseWorkspace />
            </div>
        )
    },
    'writing-style-settings': {
        padded: true,
        render: () => <WritingStyleSettings className='mx-auto h-240 max-w-229 rounded-2xl border border-border-default' />
    },
    'layout-one-column': { padded: false, render: () => <PageLayoutTemplate kind='one-column' /> },
    'layout-two-columns': { padded: false, render: () => <PageLayoutTemplate kind='two-columns' /> },
    'layout-three-columns': { padded: false, render: () => <PageLayoutTemplate kind='three-columns' /> },
    'layout-four-columns': { padded: false, render: () => <PageLayoutTemplate kind='four-columns' /> },
    'layout-main-aside': { padded: false, render: () => <PageLayoutTemplate kind='main-aside' /> },
    'layout-aside-main': { padded: false, render: () => <PageLayoutTemplate kind='aside-main' /> },
    'layout-dashboard': { padded: false, render: () => <PageLayoutTemplate kind='dashboard' /> },
    'layout-workspace': { padded: false, render: () => <PageLayoutTemplate kind='workspace' /> },
    'layout-board': { padded: false, render: () => <PageLayoutTemplate kind='board' /> },
    'layout-dashboard-dark': { padded: false, render: () => <PageLayoutTemplate kind='dashboard-dark' /> },
    'drawer-empty-sm': { padded: false, render: () => <DrawerTemplate kind='empty-sm' /> },
    'drawer-empty-default': { padded: false, render: () => <DrawerTemplate kind='empty-default' /> },
    'drawer-empty-lg': { padded: false, render: () => <DrawerTemplate kind='empty-lg' /> },
    'drawer-revenue-people': { padded: false, render: () => <DrawerTemplate kind='revenue-people' /> },
    'drawer-facebook-direct': { padded: false, render: () => <DrawerTemplate kind='facebook-direct' /> },
    'drawer-acquisition-month': { padded: false, render: () => <DrawerTemplate kind='acquisition-month' /> },
    'drawer-editor': { padded: false, render: () => <DrawerTemplate kind='editor' /> },
    'drawer-revenue-people-dark': { padded: false, render: () => <DrawerTemplate kind='revenue-people' /> },
    'drawer-facebook-direct-dark': { padded: false, render: () => <DrawerTemplate kind='facebook-direct' /> },
    'drawer-acquisition-month-dark': { padded: false, render: () => <DrawerTemplate kind='acquisition-month' /> },
    'drawer-editor-dark': { padded: false, render: () => <DrawerTemplate kind='editor' /> },
    'kanban-template-inbox': { padded: false, render: () => <KanbanTemplate kind='inbox' /> },
    'kanban-template-empty': { padded: false, render: () => <KanbanTemplate kind='empty' /> },
    'kanban-template-inbox-dark': { padded: false, render: () => <KanbanTemplate kind='inbox' /> },
    'kanban-template-empty-dark': { padded: false, render: () => <KanbanTemplate kind='empty' /> },
    'supporter-response-draft': { padded: false, render: () => <SupporterResponseTemplate kind='draft' /> },
    'supporter-response-sent': { padded: false, render: () => <SupporterResponseTemplate kind='sent' /> },
    'supporter-response-email': { padded: false, render: () => <SupporterResponseTemplate kind='email-marked-sent' /> },
    'supporter-response-writing-style': { padded: false, render: () => <SupporterResponseTemplate kind='writing-style' /> },
    'supporter-response-draft-dark': { padded: false, render: () => <SupporterResponseTemplate kind='draft' /> },
    'supporter-response-writing-style-dark': { padded: false, render: () => <SupporterResponseTemplate kind='writing-style' /> }
}
